import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Dumbbell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Equipment manager — owner/coach edits the bar weight and available plate
 * denominations for each piece of equipment. The athlete workout view uses
 * these values to compute "plates each side" inline next to prescribed
 * weights.
 *
 * Plates are entered as a comma-separated list of pounds per side, e.g.
 * "45, 35, 25, 10, 5, 2.5". The list is stored on the equipment row and
 * shared by every exercise that references that bar.
 */
export default function Equipment() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/equipment`, { withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load equipment", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onAdd = async () => {
        const name = newName.trim();
        if (!name) { toast.error("Name is required"); return; }
        setAdding(true);
        try {
            await axios.post(
                `${API}/coach/equipment`,
                { name, weight: null, plates_available: [] },
                { withCredentials: true },
            );
            toast.success(`Added "${name}"`);
            setNewName("");
            await load();
        } catch (e) {
            toast.error("Couldn't add", { description: e?.response?.data?.detail });
        } finally {
            setAdding(false);
        }
    };

    const onDelete = async (row) => {
        if (!window.confirm(`Delete "${row.name}"?`)) return;
        try {
            await axios.delete(`${API}/coach/equipment/${row.equipment_id}`, { withCredentials: true });
            toast.success("Deleted");
            setRows((rs) => rs.filter((r) => r.equipment_id !== row.equipment_id));
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-5xl mx-auto px-6 py-10">
                <div className="flex items-center gap-3 mb-2">
                    <Dumbbell className="w-6 h-6 text-brand-red" />
                    <h1 className="font-display text-4xl tracking-wider uppercase">Equipment</h1>
                </div>
                <p className="text-sm text-zinc-400 mb-8 max-w-2xl leading-relaxed">
                    Set the bar weight and the plate denominations on hand for each piece
                    of equipment. The workout runner uses these to show <span className="text-white font-semibold">plates each side</span> next
                    to every prescribed weight.
                </p>

                {/* Add new */}
                <div className="border border-zinc-800 bg-zinc-950 p-5 mb-8">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Add new equipment</div>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            data-testid="equipment-new-name"
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="e.g. Cambered Bar"
                            className="flex-1 min-w-[200px] bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                        <button
                            data-testid="equipment-add-btn"
                            type="button"
                            onClick={onAdd}
                            disabled={adding || !newName.trim()}
                            className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            Add
                        </button>
                    </div>
                </div>

                {/* Table */}
                {busy ? (
                    <div className="flex items-center justify-center py-20 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : (
                    <div className="border border-zinc-800 bg-zinc-950">
                        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 text-[10px] tracking-[0.25em] uppercase text-zinc-300 bg-black">
                            <div className="col-span-4">Name</div>
                            <div className="col-span-2 text-center">Bar weight</div>
                            <div className="col-span-5">Plates (per side, lbs)</div>
                            <div className="col-span-1 text-right">{/* actions */}</div>
                        </div>
                        {rows.length === 0 && (
                            <div className="px-4 py-10 text-center text-sm text-zinc-500">No equipment yet — add one above.</div>
                        )}
                        {rows.map((row) => (
                            <EquipmentRow key={row.equipment_id} row={row} onDelete={() => onDelete(row)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function EquipmentRow({ row, onDelete }) {
    const [weight, setWeight] = useState(row.weight != null ? String(row.weight) : "");
    const [platesCsv, setPlatesCsv] = useState((row.plates_available || []).join(", "));
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const save = async () => {
        setSaving(true);
        setSaved(false);
        try {
            const w = weight.trim() === "" ? null : Number(weight);
            if (w != null && (!Number.isFinite(w) || w < 0)) {
                toast.error("Bar weight must be a non-negative number");
                setSaving(false);
                return;
            }
            const parsed = platesCsv.split(/[,\s]+/)
                .map((s) => s.trim())
                .filter(Boolean)
                .map(Number)
                .filter((n) => Number.isFinite(n) && n > 0);
            await axios.patch(
                `${API}/coach/equipment/${row.equipment_id}`,
                { weight: w, plates_available: parsed },
                { withCredentials: true },
            );
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid={`equipment-row-${row.equipment_id}`}
            className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-900 last:border-b-0 items-center"
        >
            <div className="col-span-4">
                <div className="text-sm text-white">{row.name}</div>
                {row.seeded && <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">Seeded</div>}
            </div>
            <div className="col-span-2">
                <input
                    data-testid={`equipment-weight-${row.equipment_id}`}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.5"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="lbs"
                    className="w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm text-white text-center font-mono focus:outline-none focus:border-brand-red"
                />
            </div>
            <div className="col-span-5">
                <input
                    data-testid={`equipment-plates-${row.equipment_id}`}
                    type="text"
                    value={platesCsv}
                    onChange={(e) => setPlatesCsv(e.target.value)}
                    placeholder="45, 35, 25, 10, 5, 2.5"
                    className="w-full bg-black border border-zinc-800 px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-brand-red"
                />
            </div>
            <div className="col-span-1 flex items-center justify-end gap-1">
                <button
                    data-testid={`equipment-save-${row.equipment_id}`}
                    type="button"
                    onClick={save}
                    disabled={saving}
                    title="Save"
                    className="p-1.5 border border-zinc-800 hover:border-brand-red text-zinc-300 hover:text-white transition-colors"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : saved ? <Save className="w-3.5 h-3.5 text-emerald-400" />
                        : <Save className="w-3.5 h-3.5" />}
                </button>
                {!row.seeded && (
                    <button
                        data-testid={`equipment-delete-${row.equipment_id}`}
                        type="button"
                        onClick={onDelete}
                        title="Delete"
                        className="p-1.5 border border-zinc-800 hover:border-red-600 text-zinc-300 hover:text-red-400 transition-colors"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
